/**
 * Toasts.
 *
 * An external store rather than context, so that a mutation's `onError`
 * callback — which is not a component — can raise one without a hook.
 *
 * `critical` toasts do not auto-dismiss. A kill-switch trip or an
 * unprotected position must be acknowledged, not waited out; a message that
 * disappears on its own is a message the user is allowed to miss.
 */

import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { useSyncExternalStore, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { toApiError } from '@/api/client';
import { cn } from '@/lib/cn';

export type ToastTone = 'info' | 'success' | 'warn' | 'error' | 'critical';

export interface Toast {
  id: string;
  tone: ToastTone;
  title: string;
  message?: string;
  /** Backend request id. Rendered so the server log stays searchable. */
  requestId?: string;
  /** Milliseconds. 0 or `critical` means "until dismissed". */
  duration: number;
  action?: { label: string; onClick: () => void };
  createdAt: number;
}

type Listener = () => void;

const listeners = new Set<Listener>();
let toasts: Toast[] = [];
const timers = new Map<string, number>();

const MAX_VISIBLE = 5;

function emit(): void {
  for (const listener of listeners) listener();
}

function remove(id: string): void {
  const timer = timers.get(id);
  if (timer !== undefined) {
    window.clearTimeout(timer);
    timers.delete(id);
  }
  toasts = toasts.filter((toast) => toast.id !== id);
  emit();
}

export interface ToastInput {
  tone?: ToastTone;
  title: string;
  message?: string;
  requestId?: string;
  duration?: number;
  action?: { label: string; onClick: () => void };
}

function push(input: ToastInput): string {
  const tone = input.tone ?? 'info';
  const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const duration =
    input.duration ?? (tone === 'critical' ? 0 : tone === 'error' ? 9_000 : 4_500);

  const toast: Toast = {
    id,
    tone,
    title: input.title,
    ...(input.message === undefined ? {} : { message: input.message }),
    ...(input.requestId === undefined ? {} : { requestId: input.requestId }),
    duration,
    ...(input.action === undefined ? {} : { action: input.action }),
    createdAt: Date.now(),
  };

  // Newest first, and never let a burst of alerts fill the screen.
  toasts = [toast, ...toasts].slice(0, MAX_VISIBLE);
  emit();

  if (duration > 0) {
    timers.set(id, window.setTimeout(() => remove(id), duration));
  }
  return id;
}

export const toast = {
  show: push,
  info: (title: string, message?: string) => push({ tone: 'info', title, ...(message === undefined ? {} : { message }) }),
  success: (title: string, message?: string) =>
    push({ tone: 'success', title, ...(message === undefined ? {} : { message }) }),
  warn: (title: string, message?: string) => push({ tone: 'warn', title, ...(message === undefined ? {} : { message }) }),
  /**
   * Raise a toast from a thrown value.
   *
   * Carries the backend's own message and request id through, so a failed
   * mutation is diagnosable rather than merely annoying. Safety refusals
   * (kill-switch, limit breach) escalate to `critical` and stay on screen —
   * the platform said no for a reason.
   */
  error: (error: unknown, fallbackTitle = 'Request failed') => {
    const apiError = toApiError(error);
    return push({
      tone: apiError.isSafetyRefusal ? 'critical' : 'error',
      title: apiError.isSafetyRefusal ? 'Refused by the risk layer' : fallbackTitle,
      message: apiError.message,
      ...(apiError.requestId ? { requestId: apiError.requestId } : {}),
    });
  },
  critical: (title: string, message?: string, action?: Toast['action']) =>
    push({
      tone: 'critical',
      title,
      ...(message === undefined ? {} : { message }),
      ...(action === undefined ? {} : { action }),
      duration: 0,
    }),
  dismiss: remove,
  clear: () => {
    for (const timer of timers.values()) window.clearTimeout(timer);
    timers.clear();
    toasts = [];
    emit();
  },
};

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): Toast[] {
  return toasts;
}

const EMPTY: Toast[] = [];

export function useToasts(): Toast[] {
  return useSyncExternalStore(subscribe, getSnapshot, () => EMPTY);
}

const TONE_STYLES: Record<ToastTone, { wrapper: string; icon: ReactNode }> = {
  info: {
    wrapper: 'border-border bg-surface-2',
    icon: <Info size={15} className="text-info" aria-hidden />,
  },
  success: {
    wrapper: 'border-up/50 bg-surface-2',
    icon: <CheckCircle2 size={15} className="text-up" aria-hidden />,
  },
  warn: {
    wrapper: 'border-warn/60 bg-warn-weak',
    icon: <AlertTriangle size={15} className="text-warn" aria-hidden />,
  },
  error: {
    wrapper: 'border-danger/60 bg-surface-2',
    icon: <XCircle size={15} className="text-danger" aria-hidden />,
  },
  critical: {
    wrapper: 'border-danger bg-danger-weak',
    icon: <AlertTriangle size={15} className="text-danger" aria-hidden />,
  },
};

/**
 * The toast viewport.
 *
 * Mount once, in the app shell. Bottom-right on desktop; full-width at the
 * bottom on a phone, above the mobile nav bar so it never covers it.
 */
export function ToastViewport() {
  const items = useToasts();
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      aria-live="polite"
      aria-atomic="false"
      className="pointer-events-none fixed inset-x-2 bottom-[calc(env(safe-area-inset-bottom)+3.75rem)] z-[90] flex flex-col gap-2 sm:inset-x-auto sm:right-3 sm:bottom-3 sm:w-[360px]"
    >
      {items.map((item) => {
        const style = TONE_STYLES[item.tone];
        return (
          <div
            key={item.id}
            role={item.tone === 'critical' || item.tone === 'error' ? 'alert' : 'status'}
            className={cn(
              'slide-in pointer-events-auto flex items-start gap-2 rounded-md border px-3 py-2',
              'shadow-[var(--shadow-pop)]',
              style.wrapper,
            )}
          >
            <span className="mt-px shrink-0">{style.icon}</span>
            <div className="min-w-0 flex-1">
              <p className="text-[12px] font-semibold text-hi">{item.title}</p>
              {item.message && (
                <p className="mt-0.5 text-[11.5px] leading-snug break-words text-mid">
                  {item.message}
                </p>
              )}
              {item.requestId && (
                <p className="num mt-0.5 text-[10px] text-lo">ref {item.requestId}</p>
              )}
              {item.action && (
                <button
                  type="button"
                  onClick={() => {
                    item.action?.onClick();
                    remove(item.id);
                  }}
                  className="mt-1 text-[11px] font-medium text-accent hover:underline"
                >
                  {item.action.label}
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => remove(item.id)}
              aria-label="Dismiss notification"
              className="shrink-0 rounded p-0.5 text-lo transition-colors hover:bg-surface-3 hover:text-hi"
            >
              <X size={12} />
            </button>
          </div>
        );
      })}
    </div>,
    document.body,
  );
}
